import { Link, usePage } from "@inertiajs/react";
import { AlertTriangle, Clock, ListChecks, type LucideIcon } from "lucide-react";
import { TOP_BAR_CELL } from "@/components/layout/topBarCell";
import { tasks_path } from "@/lib/routes";
import { cn } from "@/lib/utils";

interface TaskPillCounts {
  overdue: number;
  due_soon: number;
  open: number;
}

interface State {
  label: string;
  count: number;
  icon: LucideIcon;
  className: string;
}

function stateFor(counts: TaskPillCounts): State | null {
  if (counts.overdue > 0) return { label: `${counts.overdue} overdue`, count: counts.overdue, icon: AlertTriangle, className: "bg-destructive text-destructive-foreground" };
  if (counts.due_soon > 0) return { label: `${counts.due_soon} due soon`, count: counts.due_soon, icon: Clock, className: "bg-warning-surface text-warning" };
  if (counts.open > 0) return { label: `${counts.open} open`, count: counts.open, icon: ListChecks, className: "bg-top-bar-hover text-top-bar-foreground" };
  return null;
}

// Header status, not a counter (UX doc §2 "Header cluster"): renders the
// worst open state and links to /work. Hidden at zero — "0 open" is never
// shown. A full-height top bar cell holding a pill colored by state (a solid
// red pill when overdue, so it reads on the navy bar). Below 1024 (§8) it
// collapses to icon + count, dropping the word label.
export default function TaskPill() {
  const { props } = usePage<{ task_pill?: TaskPillCounts }>();
  const counts = props.task_pill;
  const state = counts && stateFor(counts);
  if (!state) return null;

  const Icon = state.icon;

  return (
    <Link
      href={tasks_path()}
      className={cn(TOP_BAR_CELL, "whitespace-nowrap text-xs font-semibold")}
    >
      <span className={cn("flex items-center gap-1.5 rounded-full px-2.5 py-1", state.className)}>
        <Icon size={14} className="lg:hidden" aria-hidden />
        <span className="lg:hidden">{state.count}</span>
        <span className="hidden lg:inline">{state.label}</span>
      </span>
    </Link>
  );
}
